import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import MusicalService from '../../services/musicalService';
import SongAnalysisService from '../../services/songAnalysisService';
import TalentService from '../../services/talentService';
import VideoService from '../../services/videoService';
import { getSongs } from '../../services/songService';
import StatsCard from './components/StatsCard';
import { RecentActivity } from './components/RecentActivity';
import { RecentItems } from './components/RecentItems';
import RoleService from '../../services/roleService';
import { Download, Upload, FileText } from 'lucide-react';
import { exportToCSV, importFromCSV, validateCSV } from '../../services/csvService';

const AdminHomeDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, isSuperUser, isEditor, getAllUsers } = useAuth();
  const [stats, setStats] = useState({
    songs: 0,
    musicals: 0,
    roles: 0,
    talent: 0,
    videos: 0,
    users: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [csvImportModal, setCsvImportModal] = useState(false);
  const [selectedEntityType, setSelectedEntityType] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load basic stats available to all users
      const [musicalsResponse, songsResponse, songAnalysesResponse, rolesResponse] = await Promise.all([
        MusicalService?.getMusicals(),
        getSongs(),
        SongAnalysisService?.getAllSongs(),
        RoleService?.getRoles()
      ]);

      // Calculate total songs from both songs table and song_analyses
      const songsFromSongsTable = songsResponse?.data?.length || 0;
      const songsFromAnalyses = songAnalysesResponse?.data?.length || 0;
      
      const newStats = {
        musicals: musicalsResponse?.data?.length || 0,
        // Combine songs from both tables for accurate count
        songs: songsFromSongsTable + songsFromAnalyses,
        roles: rolesResponse?.data?.length || 0,
        talent: 0,
        videos: 0,
        users: 0
      };

      // Load additional stats for SuperUsers with corrected method calls
      if (isSuperUser()) {
        try {
          const [talentResponse, videoResponse, usersResponse] = await Promise.all([
            TalentService?.getAllTalentProfiles()?.catch(() => ({ data: [] })),
            VideoService?.getAllVideos()?.catch(() => ({ data: [] })),
            getAllUsers()?.catch(() => ({ data: [] }))
          ]);

          newStats.talent = talentResponse?.data?.length || 0;
          newStats.videos = videoResponse?.data?.length || 0;
          newStats.users = usersResponse?.data?.length || 0;
        } catch (adminError) {
          // If admin-specific calls fail, continue with basic stats
          console.warn('Some admin statistics could not be loaded:', adminError);
        }
      }

      setStats(newStats);

      // Generate recent activity based on user role
      generateRecentActivity(newStats);
      
    } catch (err) {
      console.error('Dashboard data loading error:', err);
      setError('Failed to load dashboard data. Please try refreshing the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const generateRecentActivity = (statsData) => {
    const activities = [];
    
    if (statsData?.songs > 0) {
      activities?.push({
        id: 1,
        type: 'song',
        title: 'Song database updated',
        description: `${statsData?.songs} songs available for analysis`,
        time: '2 hours ago',
        icon: 'Music'
      });
    }

    if (statsData?.musicals > 0) {
      activities?.push({
        id: 2,
        type: 'musical',
        title: 'Musical library updated', 
        description: `${statsData?.musicals} musicals in the database`,
        time: '5 hours ago',
        icon: 'Theater'
      });
    }

    if (isSuperUser() && statsData?.talent > 0) {
      activities?.push({
        id: 3,
        type: 'talent',
        title: 'Talent profiles synced',
        description: `${statsData?.talent} talent profiles available`,
        time: '1 day ago',
        icon: 'Star'
      });
    }

    setRecentActivity(activities?.slice(0, 5));
  };

  const getWelcomeMessage = () => {
    const roleDisplay = userProfile?.role === 'superuser' ? 'Administrator' : 
                       userProfile?.role === 'editor' ? 'Editor' : 'User';
    
    return {
      greeting: `Welcome back, ${userProfile?.full_name || user?.email || 'User'}`,
      role: roleDisplay,
      message: getContextualMessage()
    };
  };

  const getContextualMessage = () => {
    if (isSuperUser()) {
      return 'You have full administrative access to all system features.';
    } else if (isEditor()) {
      return 'You can create and edit songs and musicals.';
    } else {
      return 'You can view songs and musicals in the system.';
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'add-song': navigate('/song-addition-form');
        break;
      case 'add-musical': navigate('/add-musical');
        break;
      case 'view-songs': navigate('/song-database-list');
        break;
      case 'view-musicals': navigate('/all-musicals');
        break;
      case 'view-roles': navigate('/all-roles');
        break;
      case 'manage-talent': navigate('/talent-management-dashboard');
        break;
      case 'manage-videos': navigate('/all-videos-management');
        break;
      case 'video-analytics': navigate('/video-engagement-analytics-dashboard');
        break;
      case 'manage-users': navigate('/admin');
        break;
      default:
        break;
    }
  };

  // CSV Export Handler
  const handleExport = async (entityType) => {
    try {
      const result = await exportToCSV(entityType);
      setError(null);
      // Show success notification
      alert(`Successfully exported ${result?.count} ${entityType} to ${result?.filename}`);
    } catch (err) {
      console.error('Export error:', err);
      setError(`Failed to export ${entityType}: ${err?.message}`);
    }
  };

  // CSV Import Modal Handler
  const openImportModal = (entityType) => {
    setSelectedEntityType(entityType);
    setCsvFile(null);
    setValidationResults(null);
    setImportMessage(null);
    setCsvImportModal(true);
  };

  // CSV File Selection Handler
  const handleFileSelect = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    setCsvFile(file);
    setImportMessage(null);

    // Validate CSV file
    try {
      const results = await validateCSV(selectedEntityType, file);
      setValidationResults(results);
    } catch (err) {
      setValidationResults({
        valid: false,
        errors: [{ message: err?.message }]
      });
    }
  };

  // CSV Import Handler
  const handleImport = async () => {
    if (!csvFile || !validationResults?.valid) return;

    setImportLoading(true);
    setImportMessage(null);

    try {
      const result = await importFromCSV(selectedEntityType, csvFile);
      
      setImportMessage({
        type: 'success',
        text: `Import completed successfully!\nInserted: ${result?.inserted}\nUpdated: ${result?.updated}\nTotal: ${result?.total}`
      });

      // Refresh stats after import
      setTimeout(() => {
        loadDashboardData();
        setCsvImportModal(false);
      }, 2000);
    } catch (err) {
      console.error('Import error:', err);
      setImportMessage({
        type: 'error',
        text: `Import failed: ${err?.message}`
      });
    } finally {
      setImportLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-7xl">
          <div className="animate-pulse">
            <div className="h-8 bg-white bg-opacity-20 rounded-xl w-64 mb-4"></div>
            <div className="h-4 bg-white bg-opacity-15 rounded-xl w-96 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[1, 2, 3, 4]?.map((i) => (
                <div key={i} className="h-32 bg-white bg-opacity-10 rounded-xl"></div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const welcomeInfo = getWelcomeMessage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
      <Header />
      <main className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-heading font-bold text-white mb-2">
                {welcomeInfo?.greeting}
              </h1>
              <div className="flex items-center space-x-4">
                <span className="px-3 py-1 bg-blue-500 bg-opacity-30 text-blue-100 text-sm rounded-xl font-medium">
                  {welcomeInfo?.role}
                </span>
                <p className="text-blue-100 font-body">
                  {welcomeInfo?.message}
                </p>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={() => navigate('/my-account')}
              iconName="Settings"
              iconPosition="left"
              className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-xl"
            >
              Account Settings
            </Button>
          </div>

          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-4 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadDashboardData}
                className="mt-3 border-red-400 text-red-600 hover:bg-red-400 hover:text-white rounded-lg"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Retry
              </Button>
            </div>
          )}
        </div>

        {/* Stats Grid with CSV Export */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="relative">
            <StatsCard 
              label="Songs" 
              value={stats?.songs} 
              icon="Music"
              entityType="songs"
              title="Songs"
              description="Total songs in database"
            />
            <div className="absolute top-2 right-2 flex gap-1">
              <button
                onClick={() => handleExport('songs')}
                className="p-1.5 bg-white/80 hover:bg-green-50 rounded-lg shadow-sm transition-colors"
                title="Export Songs to CSV"
              >
                <Download className="w-4 h-4 text-green-600" />
              </button>
              <button
                onClick={() => openImportModal('songs')}
                className="p-1.5 bg-white/80 hover:bg-blue-50 rounded-lg shadow-sm transition-colors"
                title="Import Songs from CSV"
              >
                <Upload className="w-4 h-4 text-blue-600" />
              </button>
            </div>
          </div>

          <div className="relative">
            <StatsCard 
              label="Musicals" 
              value={stats?.musicals} 
              icon="Theater"
              entityType="musicals"
              title="Musicals"
              description="Total musicals in database"
            />
            <div className="absolute top-2 right-2 flex gap-1">
              <button
                onClick={() => handleExport('musicals')}
                className="p-1.5 bg-white/80 hover:bg-green-50 rounded-lg shadow-sm transition-colors"
                title="Export Musicals to CSV"
              >
                <Download className="w-4 h-4 text-green-600" />
              </button>
              <button
                onClick={() => openImportModal('musicals')}
                className="p-1.5 bg-white/80 hover:bg-blue-50 rounded-lg shadow-sm transition-colors"
                title="Import Musicals from CSV"
              >
                <Upload className="w-4 h-4 text-blue-600" />
              </button>
            </div>
          </div>

          <div className="relative">
            <StatsCard 
              label="Roles" 
              value={stats?.roles} 
              icon="Users"
              entityType="roles"
              title="Roles"
              description="Total roles in database"
            />
            <div className="absolute top-2 right-2 flex gap-1">
              <button
                onClick={() => handleExport('roles')}
                className="p-1.5 bg-white/80 hover:bg-green-50 rounded-lg shadow-sm transition-colors"
                title="Export Roles to CSV"
              >
                <Download className="w-4 h-4 text-green-600" />
              </button>
              <button
                onClick={() => openImportModal('roles')}
                className="p-1.5 bg-white/80 hover:bg-blue-50 rounded-lg shadow-sm transition-colors"
                title="Import Roles from CSV"
              >
                <Upload className="w-4 h-4 text-blue-600" />
              </button>
            </div>
          </div>

          {isSuperUser() && (
            <div className="relative">
              <StatsCard 
                label="Talent" 
                value={stats?.talent} 
                icon="Star"
                entityType="talent"
                title="Talent"
                description="Total talent profiles in database"
              />
              <div className="absolute top-2 right-2 flex gap-1">
                <button
                  onClick={() => handleExport('talent')}
                  className="p-1.5 bg-white/80 hover:bg-green-50 rounded-lg shadow-sm transition-colors"
                  title="Export Talent to CSV"
                >
                  <Download className="w-4 h-4 text-green-600" />
                </button>
                <button
                  onClick={() => openImportModal('talent')}
                  className="p-1.5 bg-white/80 hover:bg-blue-50 rounded-lg shadow-sm transition-colors"
                  title="Import Talent from CSV"
                >
                  <Upload className="w-4 h-4 text-blue-600" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Items - Replacing Quick Actions */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <h2 className="text-xl font-heading font-semibold text-white mb-6 flex items-center space-x-2">
              <Icon name="Clock" size={24} />
              <span>Recent Items</span>
            </h2>
            
            <RecentItems
              userRole={userProfile?.role}
              isEditor={isEditor()}
              isSuperUser={isSuperUser()}
            />
          </div>

          {/* Recent Activity */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
            <h2 className="text-xl font-heading font-semibold text-white mb-6 flex items-center space-x-2">
              <Icon name="Activity" size={24} />
              <span>System Overview</span>
            </h2>
            
            <RecentActivity 
              activities={recentActivity}
              userRole={userProfile?.role}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-white border-opacity-20">
          <div className="flex items-center justify-between text-sm text-blue-100">
            <div className="flex items-center space-x-2">
              <Icon name="Database" size={16} />
              <span>
                Last updated: {new Date()?.toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Users" size={16} />
              <span>
                Logged in as {userProfile?.role === 'superuser' ? 'Administrator' : 
                              userProfile?.role === 'editor' ? 'Editor' : 'User'}
              </span>
            </div>
          </div>
        </div>
      </main>
      {/* CSV Import Modal */}
      {csvImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Import {selectedEntityType?.replace('_', ' ')}
                  </h3>
                  <p className="text-sm text-gray-600">Upload CSV file to import or update records</p>
                </div>
              </div>
              <button
                onClick={() => setCsvImportModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <span className="text-2xl text-gray-500">×</span>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-2">
                  CSV file should include all required fields. Records with ID will be updated, records without ID will be created.
                </p>
              </div>

              {/* Validation Results */}
              {validationResults && (
                <div className={`p-4 rounded-lg ${validationResults?.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${validationResults?.valid ? 'bg-green-100' : 'bg-red-100'}`}>
                      {validationResults?.valid ? (
                        <span className="text-green-600 text-sm">✓</span>
                      ) : (
                        <span className="text-red-600 text-sm">✕</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-semibold ${validationResults?.valid ? 'text-green-900' : 'text-red-900'}`}>
                        {validationResults?.valid ? 'Validation Passed' : 'Validation Failed'}
                      </h4>
                      <p className="text-sm text-gray-700 mt-1">
                        Total rows: {validationResults?.totalRows}
                      </p>

                      {/* Errors */}
                      {validationResults?.errors?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-red-900 mb-2">Errors:</p>
                          <ul className="space-y-1">
                            {validationResults?.errors?.slice(0, 5)?.map((error, idx) => (
                              <li key={idx} className="text-sm text-red-700">
                                {error?.row && `Row ${error?.row}: `}
                                {error?.field && `${error?.field} - `}
                                {error?.message}
                              </li>
                            ))}
                            {validationResults?.errors?.length > 5 && (
                              <li className="text-sm text-red-700 font-medium">
                                ... and {validationResults?.errors?.length - 5} more errors
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Warnings */}
                      {validationResults?.warnings?.length > 0 && validationResults?.valid && (
                        <div className="mt-3">
                          <p className="text-sm font-medium text-yellow-900 mb-2">Note:</p>
                          <ul className="space-y-1">
                            {validationResults?.warnings?.slice(0, 3)?.map((warning, idx) => (
                              <li key={idx} className="text-sm text-yellow-700">
                                Row {warning?.row}: {warning?.message}
                              </li>
                            ))}
                            {validationResults?.warnings?.length > 3 && (
                              <li className="text-sm text-yellow-700">
                                ... and {validationResults?.warnings?.length - 3} more rows
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Import Message */}
              {importMessage && (
                <div className={`p-4 rounded-lg ${importMessage?.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`text-sm whitespace-pre-line ${importMessage?.type === 'success' ? 'text-green-900' : 'text-red-900'}`}>
                    {importMessage?.text}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={() => setCsvImportModal(false)}
                className="px-6 py-2.5 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!csvFile || !validationResults?.valid || importLoading}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                  !csvFile || !validationResults?.valid || importLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' :'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {importLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Importing...
                  </span>
                ) : (
                  'Import CSV'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHomeDashboard;